import React, { PropsWithChildren, useMemo } from 'react'
import Image from 'next/image'
import { Heading, Section } from '@alto-avios/alto-ui'
import { ContentfulImageSize, formatContentfulImageSrc } from '~/utils/assets/contentfulImage'
import { HeroProps } from './Hero.types'
import styles from './Hero.module.scss'

export const sizes: ContentfulImageSize[] = [
  { width: 1440, height: 400, media: '(min-width: 1280px)', densities: [1, 2] },
  { width: 1280, height: 400, media: '(min-width: 960px)', densities: [1, 2] },
  { width: 960, height: 400, media: '(min-width: 768px)', densities: [1, 2] },
  { width: 768, height: 376, media: '(min-width: 650px)', densities: [1, 2] },
  { width: 650, height: 228, media: '(min-width: 475px)', densities: [1, 2] },
  { width: 475, height: 228, media: '(max-width: 474px)', densities: [1, 2] },
]

const smallestSize = sizes[sizes.length - 1]

function Headline({ children }: Readonly<PropsWithChildren>) {
  return (
    <Heading as="h1" size="xl" key={`${children}-text`} foregroundColor="accentOnVibrant">
      {children}
    </Heading>
  )
}

function Tagline({ children, ...rest }: Readonly<PropsWithChildren>) {
  return (
    <div className={styles.tagline} {...rest}>
      {children}
    </div>
  )
}

/**
 * Animation implementation details should not be tested with jest
 * these will be marked with istanbul ignore comments
 * */

export default function Hero({
  src,
  alt,
  contentType,
  shouldFormatSrc = true,
  children,
}: PropsWithChildren<HeroProps>) {
  return useMemo(
    () => (
      <div id="Hero" data-testid="hero-component" className={styles.wrapper}>
        <picture data-testid="hero-image" key={`${alt}-image`} className={styles.picture}>
          {src && shouldFormatSrc
            ? sizes.map((size) => (
                <React.Fragment key={size.media}>
                  <source
                    media={size.media}
                    type="image/webp"
                    srcSet={formatContentfulImageSrc(src, size, 'webp')}
                  />
                  <source
                    media={size.media}
                    type="image/jpeg"
                    srcSet={formatContentfulImageSrc(src, size, 'jpg')}
                  />
                </React.Fragment>
              ))
            : null}
          {src && !shouldFormatSrc && contentType
            ? sizes.map((size) => (
                <source key={size.media} media={size.media} type={contentType} srcSet={src} />
              ))
            : null}
          {src ? (
            <Image
              src={
                shouldFormatSrc
                  ? formatContentfulImageSrc(src, {
                      width: smallestSize.width,
                      height: smallestSize.height,
                    })
                  : src
              }
              alt={alt}
              layout="fill"
              className={styles.image}
              unoptimized={shouldFormatSrc ? undefined : true}
            />
          ) : null}
        </picture>
        <Section.Container
          className={styles.body}
          justifyContent="flexStart"
          alignItems="flexStart"
          flexDirection="column"
        >
          {children}
        </Section.Container>
      </div>
    ),
    [alt, children, contentType, shouldFormatSrc, src]
  )
}

Hero.Headline = Headline
Hero.Tagline = Tagline
